import "./views/settings-view";
import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { api } from "./api/client";
import { session } from "./auth/session";
import { currentModule, isDocs, navigate, type ModuleName } from "./router";
import "./components/app-shell";
import "./components/page-heading";
import "./components/surface-window";
import "./views/login-view";
import "./views/overview-view";
import "./views/knowledge-view";
import "./views/rag-view";
import "./views/operations-views";
import "./views/access-views";
import "./views/codex-view";
import "./views/skills-view";
import "./views/source-repos-view";
import "./views/api-explorer";

@customElement("campat-app")
export class CampatApp extends LitElement {
  @state() private authenticated = false;
  @state() private restoring = true;
  @state() private active: ModuleName = currentModule();
  private bootstrapFrame = 0;
  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("popstate", this.routeChanged);
    this.bootstrapFrame = window.requestAnimationFrame(() => {
      this.bootstrapFrame = 0;
      void this.restoreSession();
    });
  }
  private async restoreSession() {
    if (!session.hasHint && !isDocs()) {
      this.restoring = false;
      return;
    }
    try {
      const { user } = await api.me();
      session.set(user);
      this.authenticated = true;
      if (!isDocs()) {
        this.active = currentModule();
        navigate(this.active, true);
      }
    } catch {
      session.clear();
    } finally {
      this.restoring = false;
    }
  }
  disconnectedCallback() {
    if (this.bootstrapFrame) window.cancelAnimationFrame(this.bootstrapFrame);
    window.removeEventListener("popstate", this.routeChanged);
    super.disconnectedCallback();
  }
  private routeChanged = () => {
    this.active = currentModule();
  };

  private view() {
    const views: Record<ModuleName, unknown> = {
      overview: html`<overview-view></overview-view>`,
      knowledge: html`<knowledge-view></knowledge-view>`,
      rag: html`<rag-view></rag-view>`,
      jobs: html`<jobs-view></jobs-view>`,
      history: html`<jobs-view></jobs-view>`,
      logs: html`<logs-view></logs-view>`,
      keys: html`<keys-view></keys-view>`,
      users: html`<users-view></users-view>`,
      codex: html`<codex-view></codex-view>`,
      skills: html`<skills-view></skills-view>`,
      sources: html`<source-repos-view></source-repos-view>`,
      settings: html`<settings-view></settings-view>`,
    };
    return views[this.active];
  }

  private async logout() {
    await api.logout().catch(() => undefined);
    session.clear();
    this.authenticated = false;
  }

  private authenticatedUser(event: CustomEvent) {
    session.set(event.detail);
    this.authenticated = true;

    // Preserve a directly requested console module through authentication.
    this.active = currentModule();
    navigate(this.active, true);
  }

  render() {
    if (isDocs()) {
      return html`<api-explorer @logout=${this.logout}></api-explorer>`;
    }

    if (this.restoring) {
      return html`
        <a2k-desktop class="login-desktop">
          <div class="restore-stage">
            <a2k-progress></a2k-progress>
          </div>
        </a2k-desktop>
      `;
    }

    if (!this.authenticated) {
      return html`
        <login-view @authenticated=${this.authenticatedUser}></login-view>
      `;
    }

    return html`
      <app-shell
        active=${this.active}
        .content=${this.view()}
        @navigate=${(event: CustomEvent<ModuleName>) => navigate(event.detail)}
        @logout=${this.logout}
      ></app-shell>
    `;
  }
}
